import { useMemo, useState } from 'react'

import { useCreateCase } from '../hooks/useCreateCase'
import { usePermissions } from '@/hooks/usePermissions'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { HiOutlineCheckCircle } from 'react-icons/hi'

import { Box } from '@chakra-ui/react'

import {
  MultiPartForm,
  MultiSelectInput,
  MultilineTextInput,
  TextInput,
} from '@hivewatch/dark'
import type { ColorScheme, FormSectionDef } from '@hivewatch/dark'

import {
  Button,
  FacilityOptionLabel,
  FormInputControl,
  FormInputSelectControl,
  FormTextareaControl,
} from '@/components/ui'
import {
  FacilityOptionWithAddressI,
  createFacilityAddressFilter,
} from '@/utils/forms/optionBuilders'
import { getFacilityNoOptionsMessage } from '@/utils/forms/validation'

import { CreateCaseFormInputsIProps } from '../types/types'
import { SelectedFacilityPill } from './SelectedFacilityPill'

interface CreateCaseFormIProps {
  incidentId?: string
  onClose: () => void
  isRebrand?: boolean
  colorScheme?: ColorScheme
}

export const CreateCaseForm = ({
  incidentId,
  onClose,
  isRebrand = false,
  colorScheme = 'dark',
}: CreateCaseFormIProps) => {
  const { t } = useTranslation()
  const { shouldEnableCaseManagement } = usePermissions()
  const {
    onCreateCase,
    setFacilityName,
    isFacilitiesLoading,
    isFacilitiesError,
    isLoading,
    facilityOptions,
  } = useCreateCase({
    incidentId,
    onClose,
  })

  // ControlledMultiSelectInput/MultiSelectInput's subtitle/caption are plain
  // presentation strings, so the "matched by address" cue is translated here
  // rather than baked into getIncidentFacilityOptionsWithAddress (which stays
  // i18n-agnostic).
  const facilityOptionsWithCue = useMemo(
    () =>
      (facilityOptions as FacilityOptionWithAddressI[]).map((option) => ({
        ...option,
        caption:
          option.matchedField === 'address'
            ? t('common:facility_matched_by_address')
            : undefined,
      })),
    [facilityOptions, t]
  )

  // Dark form controlled state
  const [darkName, setDarkName] = useState('')
  const [darkDescription, setDarkDescription] = useState('')
  const [selectedFacilities, setSelectedFacilities] = useState<string[]>([])

  const handleDarkSubmit = () => {
    const facilities = selectedFacilities.map((id) => {
      const opt = facilityOptions.find((o: { value: string }) => o.value === id)
      return { value: id, label: opt?.label ?? id }
    })
    onCreateCase({
      name: darkName,
      description: darkDescription,
      facilities,
    })
  }

  const isDarkFormValid =
    darkName.trim().length > 0 &&
    selectedFacilities.length > 0 &&
    darkDescription.trim().length > 0

  // Legacy form (react-hook-form)
  const { handleSubmit, register, control, errors, formState } =
    useForm<CreateCaseFormInputsIProps>({
      mode: 'onChange',
      reValidateMode: 'onChange',
    })
  const { isValid } = formState

  if (isRebrand) {
    const sections: FormSectionDef[] = [
      {
        title: t('cases:crud_case_name_label'),
        body: (
          <TextInput
            colorScheme={colorScheme}
            inputProps={{ 'aria-label': t('cases:crud_case_name_label') }}
            onChange={(e) => setDarkName(e.target.value)}
            placeholder={t('cases:crud_case_name_placeholder')}
            value={darkName}
            width='100%'
          />
        ),
      },
      {
        title: t('cases:crud_facilities_label'),
        body: (
          <MultiSelectInput
            aria-label={t('cases:crud_facilities_label')}
            colorScheme={colorScheme}
            // The facility search is server-side and also matches by
            // address, so an address-only match's label may not contain the
            // typed query — 'internal' mode would re-filter it away.
            filterMode='external'
            intentLabel={t('cases:crud_search_facilities')}
            maxResults={7}
            noMatchLabel={t('cases:crud_no_facilities_found')}
            onClear={() => setSelectedFacilities([])}
            onQueryChange={setFacilityName}
            onRemove={(value) =>
              setSelectedFacilities((prev) => prev.filter((v) => v !== value))
            }
            onToggleOption={(value) => {
              setSelectedFacilities((prev) =>
                prev.includes(value)
                  ? prev.filter((v) => v !== value)
                  : [...prev, value]
              )
            }}
            options={facilityOptionsWithCue}
            placeholder={t('cases:crud_facilities_placeholder')}
            renderSelectedItem={({ item, onRemove }) => (
              <SelectedFacilityPill
                colorScheme={colorScheme}
                label={item.label}
                onRemove={onRemove}
              />
            )}
            resultsHeader={t('cases:crud_select_facilities')}
            searchPlaceholder={t('cases:crud_search_facilities')}
            searchable
            selected={selectedFacilities}
          />
        ),
      },
      {
        title: t('cases:crud_brief_summary_label'),
        body: (
          <MultilineTextInput
            aria-label={t('cases:crud_brief_summary_label')}
            colorScheme={colorScheme}
            onChange={(e) => setDarkDescription(e.target.value)}
            placeholder={t('cases:crud_brief_summary_placeholder')}
            textStyle='small-body'
            value={darkDescription}
            width='100%'
          />
        ),
      },
    ]

    return (
      <MultiPartForm
        colorScheme={colorScheme}
        completeButtonText={t('cases:crud_create_case')}
        fillHeight
        isCompleteDisabled={!shouldEnableCaseManagement || !isDarkFormValid}
        isCompleteLoading={isLoading}
        onComplete={handleDarkSubmit}
        sections={sections}
        title=''
      />
    )
  }

  return (
    <Box>
      <Box py={2}>
        <Box mb={3}>
          <FormInputControl
            errorMessage={errors.name && errors.name.message}
            id='name'
            inputRef={register({
              required: 'Case name is required',
            })}
            isDisabled={isLoading}
            isInvalid={!!errors.name}
            label='Case Name'
            placeholder='Enter a name for this Case'
          />
        </Box>
        <Box mb={3}>
          <FormInputSelectControl
            closeMenuOnSelect={false}
            control={control}
            defaultValue={null}
            errorMessage={!!errors?.facilities && 'Facilities are required'}
            filterOption={createFacilityAddressFilter}
            formatOptionLabel={(option, meta) => (
              <FacilityOptionLabel
                isValueContext={meta?.context === 'value'}
                option={option}
              />
            )}
            id='facilities'
            isClearable
            isDisabled={isLoading}
            isInvalid={!!errors.facilities}
            isLoading={isFacilitiesLoading}
            isMulti
            isSearchable
            label='Facilities'
            noOptionsMessage={({ inputValue }) =>
              getFacilityNoOptionsMessage(
                inputValue,
                isFacilitiesError,
                t('common:facility_search_error')
              )
            }
            onInputChange={setFacilityName}
            options={facilityOptions}
            placeholder='Add facilities where this Case applies'
            rules={{ required: true }}
          />
        </Box>
        <Box mb={3}>
          <FormTextareaControl
            errorMessage={errors.description && errors.description.message}
            id='description'
            isInvalid={!!errors.description}
            label='Brief Summary'
            labelFontWeight='500'
            placeholder='Enter brief summary'
            textareaRef={register({
              required: 'Brief summary is required',
            })}
          />
        </Box>
      </Box>

      <Box display='flex' justifyContent='flex-end'>
        <Button mr={3} onClick={onClose} variant='secondary'>
          Cancel
        </Button>
        <Button
          isDisabled={!shouldEnableCaseManagement || !isValid}
          isLoading={isLoading}
          leftIcon={<HiOutlineCheckCircle size='24px' />}
          loadingText='Creating Case'
          onClick={handleSubmit(onCreateCase)}
        >
          Create Case
        </Button>
      </Box>
    </Box>
  )
}
